import React from "react";
import ReactApexChart from "react-apexcharts";
import { useTitle } from "../../../../hooks/useTitle";
import { withAuth } from "../../../../components/HOC/withAuth";
import CardContainer from "../../../../components/CustomUI/CardContainer";
import { SectionSpinner, SectionEmpty, KpiCard } from "../shared/utils";
import QualityReportShell from "./QualityReportShell";
import { useQualityReport } from "./useQualityReport";

const CHART = {
  rejected: "#dc2626",
  rate: "#2563eb",
  produced: "#0f766e",
};

const QualityTrendReport = () => {
  useTitle("Rovion MES - Özet Raporu");
  const {
    filters,
    machines,
    shifts,
    rows,
    loading,
    derived,
    handleApply,
    toasts,
    dismissToast,
  } = useQualityReport();

  const content = () => {
    if (loading) return <SectionSpinner />;
    if (!rows.length || !derived) return <SectionEmpty />;
    const { dateMap, dateKeys, summary } = derived;
    if (dateKeys.length < 1) return <SectionEmpty />;

    const maxRefusePct = Math.max(
      ...dateKeys.map((d) => dateMap[d].refusePct),
      summary.refusePct,
    );
    const pctAxisMax = Math.min(
      100,
      Math.max(10, Math.ceil(maxRefusePct * 1.25)),
    );

    return (
      <div className="d-flex flex-column gap-2">
        <div className="d-flex w-100 gap-2">
          <KpiCard
            label="Toplam Üretim"
            value={summary.produced.toLocaleString("tr-TR")}
            // sub="Sensör kayıtları (seçili aralık)"
            referenceColor={CHART.produced}
          />
          <KpiCard
            label="Toplam Hatalı"
            value={summary.rejected.toLocaleString("tr-TR")}
            // sub="Kalite girişleri (makine/vardiya)"
            referenceColor={CHART.rejected}
          />
          <KpiCard
            label="Ort. Red Oranı"
            value={`${summary.refusePct}%`}
            // sub="Hatalı ÷ üretim"
            referenceColor={CHART.rate}
          />
        </div>

        <CardContainer title="Özet Raporu">
          <ReactApexChart
            type="line"
            series={[
              {
                name: "Hatalı Üretim Sayısı",
                type: "column",
                data: dateKeys.map((d) => dateMap[d].rejected),
              },
              {
                name: "Hata Oranı %",
                type: "line",
                data: dateKeys.map((d) => dateMap[d].refusePct),
              },
            ]}
            options={{
              chart: {
                type: "line",
                toolbar: { show: false },
                fontFamily: "inherit",
                foreColor: "#334155",
              },
              colors: [CHART.rejected, CHART.rate],
              plotOptions: { bar: { columnWidth: "70%", borderRadius: 3 } },
              fill: { opacity: [0.9, 1] },
              markers: {
                size: [0, 5],
                colors: [CHART.rejected, CHART.rate],
                strokeColors: "#fff",
                strokeWidth: 2,
                hover: { sizeOffset: 2 },
              },
              xaxis: {
                categories: dateKeys,
                labels: { rotate: -45, style: { fontSize: "10px" } },
              },
              stroke: {
                width: [0, 3],
                curve: "smooth",
                colors: [CHART.rejected, CHART.rate],
              },
              yaxis: [
                {
                  title: {
                    text: "Hatalı Adet",
                    style: { color: CHART.rejected, fontWeight: 600 },
                  },
                  labels: {
                    formatter: (v) => Math.round(v),
                    style: { colors: CHART.rejected },
                  },
                },
                {
                  opposite: true,
                  min: 0,
                  max: pctAxisMax,
                  title: {
                    text: "Red %",
                    style: { color: CHART.rate, fontWeight: 600 },
                  },
                  labels: {
                    formatter: (v) => `${v}%`,
                    style: { colors: CHART.rate },
                  },
                },
              ],
              legend: { position: "top" },
              dataLabels: { enabled: false },
              grid: { borderColor: "#f0f2f5" },
              tooltip: {
                shared: true,
                intersect: false,
                custom: ({ dataPointIndex }) => {
                  const d = dateKeys[dataPointIndex];
                  const day = dateMap[d];
                  return `<div class="p-2 small">
                    <strong>${d}</strong><br/>
                    Üretilen: ${day.produced.toLocaleString("tr-TR")}<br/>
                    Hatalı: ${day.rejected.toLocaleString("tr-TR")}<br/>
                    Red Oranı: ${day.refusePct}%
                  </div>`;
                },
              },
            }}
            height={300}
          />
        </CardContainer>
      </div>
    );
  };

  return (
    <QualityReportShell
      title="Özet Raporu"
      filters={filters}
      machines={machines}
      shifts={shifts}
      onApply={handleApply}
      toasts={toasts}
      dismissToast={dismissToast}
    >
      {content()}
    </QualityReportShell>
  );
};

export default withAuth(QualityTrendReport);
